import React from "react";
import { Card, Row, Col, Tag } from "antd";
import { CheckCircleOutlined, ClockCircleOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useScanQrcodeStore } from "@/store";
import { isEmpty, formatDate } from "@/utils";
import { STAMP_STATUS } from "@/constants";

interface VerifyInfoProps {
  className?: string;
}

const VerifyInfo: React.FC<VerifyInfoProps> = ({ className = "" }) => {
  const { t } = useTranslation();
  const { stamp_code, customer } = useScanQrcodeStore();

  if (isEmpty(stamp_code) || isEmpty(customer)) {
    return null;
  }

  const { verification_code, status, first_verification_at, has_warranty, warranty_starts_at, warranty_ends_at } =
    stamp_code;

  const getStatusTag = (status: string) => {
    switch (status) {
      case STAMP_STATUS.ACTIVATED:
        return (
          <Tag icon={<CheckCircleOutlined />} color="success">
            {t("common.verified")}
          </Tag>
        );
      case STAMP_STATUS.WARRANTY_PROCESSING:
        return (
          <Tag icon={<ClockCircleOutlined />} color="processing">
            {t("common.warranty_processing")}
          </Tag>
        );
      case STAMP_STATUS.WARRANTY_REPLACED:
        return (
          <Tag icon={<CheckCircleOutlined />} color="success">
            {t("common.warranty_replaced")}
          </Tag>
        );
      default:
        return <Tag color="default">{status}</Tag>;
    }
  };

  return (
    <Card className={className} title={t("common.verification_info")}>
      <Row gutter={[16, 16]}>
        {verification_code && (
          <Col span={24}>
            <div className="mb-2">
              <span className="font-medium">{t("common.verification_code")}:</span>
              <span className="ml-2 font-mono bg-gray-100 px-2 py-1 rounded">{verification_code}</span>
            </div>
          </Col>
        )}

        {status && (
          <Col span={24}>
            <div className="mb-2">
              <span className="font-medium">{t("common.status")}:</span>
              <span className="ml-2">{getStatusTag(status)}</span>
            </div>
          </Col>
        )}

        {first_verification_at && (
          <Col span={24}>
            <div className="mb-2">
              <span className="font-medium">{t("common.first_verification")}:</span>
              <span className="ml-2">{formatDate(first_verification_at)}</span>
            </div>
          </Col>
        )}

        {has_warranty && (
          <Col span={24}>
            <div className="mb-2">
              <span className="font-medium">{t("common.warranty_info")}:</span>
              <div className="ml-2 mt-1">
                <Tag icon={<CheckCircleOutlined />} color="green">
                  {t("common.has_warranty")}
                </Tag>
                {warranty_starts_at && (
                  <div className="text-sm text-gray-600 mt-1">
                    {t("common.warranty_period")}: {formatDate(warranty_starts_at)} - {formatDate(warranty_ends_at)}
                  </div>
                )}
              </div>
            </div>
          </Col>
        )}
      </Row>
    </Card>
  );
};

export default VerifyInfo;
